// tslint:disable: no-string-literal
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ViewState, CatalogList } from './store/+state/stores.reducer';
import {
  ViewStateUpdate,
  SelectCategory,
  ViewOrderItem,
  ErrorMessage,
  ViewOrderStatus,
  ValidateStoreLocations,
  ScrollToCategory,
  SetPendingSharedItem,
  SetPendingSharedCategory,
  ClearPendingSharedItem,
} from './store/+state/stores.actions';
import { Location } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  UrlTree,
} from '@angular/router';
import {
  getCurrentError,
  getSelectedStoreCatalog,
  getCurrentCartState,
  getStoreLocationsState,
  getSelectedLang,
  getPendingSharedItem,
  getCurrentStoreViewStatus,
} from './store/+state/stores.selectors';
import { getSelectedStore } from './store/+state/stores.selectors';
import {
  PendingSharedItemReference,
  sharedReferenceKind,
} from './store/+state/stores.reducer';
import { StoreService } from './store/store.service';
import {
  ClientStore,
  LocationType,
  Order,
  StoreViewState,
} from '../stores/stores';
import { combineLatest, Subject } from 'rxjs';
import { WindowRefService } from '../window.service';
import { CompleteTrustPaymentsPayment } from './payments/+state/payment.actions';
import { TrustPaymentsPaymentInfo } from './payments/payment.types';
import { environment } from '../../environments/environment';
import { APPMODE } from '../../environments/appmode.enum';
import { WhitelabelService } from '../shared/whitelabel.service';
import { take, takeUntil, timeout, withLatestFrom } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { VisitCookieService } from './visit-cookie.service';
import {
  buildUrlWithoutLocationLabel,
  hasLocationInHash,
} from './store/utils/LocationUrlUtils';
import {
  isWidgetPageState,
  isWidgetRouteAvailable,
  widgetViewFragmentForState,
} from './store/utils/WidgetPageUtils';
import { HelperService } from './helper.service';
import { isCustomerUiPath } from './store/utils/CustomerUiContext';
import {
  getQueryParamFromUrl,
  removeQueryParamFromUrl,
} from '../shared/utils/UrlQueryUtils';

@Injectable({
  providedIn: 'root',
})
export class LocationService implements OnDestroy {
  private destroy$ = new Subject<void>();
  selectedStore: ClientStore;
  selectedLang: string;
  catalog: CatalogList;
  cart: Order;
  refreshAction: string = null;
  refreshActionAfterLocationValidation: string = null;
  static orderUuid: string = null;
  static bookingId: number;
  locale: string = null;
  storeLocation: string = null;
  basketEnabled: boolean = null;
  private storeLocationUrl: string = null;
  trustPaymentsTransactionReference: string = null;
  private locationTypeSelected: LocationType;
  private currentView: string = '';

  // Cached cookie values to prevent unnecessary re-renders
  private cachedLocationLabel: string | null = null;
  private cachedLocationType: 'url-location' | 'cart-location' | null = null;
  private cachedLocationOrder: string | null = null;
  private lastStoreAliasName: string | null = null;
  private isCacheValid: boolean = false;

  // Printer name cache (stored from ?printer=... in customer URL)
  private cachedPrinterName: string | null = null;
  private cachedPrinterStoreId: number | null = null;
  private isPrinterCacheValid: boolean = false;

  private lastLoadViewPath: string | null = null;
  private lastLoadViewTime: number = 0;

  private hydrationEchoAnchor: string | null = null;
  private hydrationEchoTime: number = 0;
  private static readonly HYDRATION_ECHO_WINDOW_MS = 2000;

  // #11277 - how many in-app entries sit behind each history entry.
  //
  // goBack needs browser history depth; navigationId only counts navigations, and
  // the two part company on every replaceUrl / skipLocationChange, which bump the
  // id without adding an entry to go back to. Reading the id as depth is what sent
  // the customer out of the store here, and at six other replaceUrl call sites.
  //
  // Keyed by navigationId rather than held as a single counter so that returning to
  // an older entry restores that entry's depth instead of guessing at it. The key
  // needs care: the router NEVER lands back on an entry's old id - every popstate
  // re-stamps the entry with the new transition's id before NavigationEnd fires
  // (setBrowserUrl's replace branch). The id the entry used to carry arrives in
  // NavigationStart.restoredState, which the router docs provide for exactly this,
  // so that is captured per navigation and the entry is re-keyed on arrival.
  private navigationDepthByNavId = new Map<number, number>();

  // Depth of the entry we are standing on. The boot navigation is replaceUrl
  // (navigateToSyncWithBrowser), so it never increments - the arrival entry sits
  // at this seed, depth 0: nothing in-app behind it.
  private navigationDepth = 0;

  // restoredState.navigationId from the NavigationStart of the navigation that is
  // currently completing - the id the returned-to entry carried on its last visit.
  private restoredNavigationId: number | null = null;

  // Terminal / confirmation views reached once at the end of an order flow.
  // The loadView same-path dedupe must never suppress a render for these
  // (see loadView + card #10584): store-loading legitimately re-issues
  // loadView for the same path as the store finishes loading, and dropping
  // that repeat strands the render (e.g. the Thank You page never appears).
  static readonly TERMINAL_VIEW_KEYWORDS = [
    'thankyou',
    'paymentProgress',
    'paymentPay',
    'qrCodePay',
    'standalone-payment',
  ];

  // Matches a `#l/<location>` segment in the URL hash. Shared between the
  // invalid-location test and the strip so they can't drift.
  static readonly LOCATION_HASH_SEGMENT = /#l\/[^\/]+\/?/;

  currentLoadView: string = '';

  // Story #10478 - guards against firing the resolve+redirect twice for the same
  // pending reference on the same branch.
  private resolvingSharedItemKey: string | null = null;
  private hydratedSharedItemKey: string | null = null;
  // Story #11520 - true between hydrating a shared CATEGORY reference on a branch
  // and the resolve answering. A `#category/…` fragment is ordinary during menu
  // browsing, so — unlike `#offer` — it cannot be used to suppress the default-case
  // LOADED dispatch; without this flag a stray default-case render would drop the
  // loader and flash the menu top before the jump to the category.
  private holdingViewForSharedCategory = false;
  // Story #11520 - the watchdog that guarantees the hold above is temporary. The
  // resolve is the normal way out of it, but three states never reach the resolve
  // at all: a hand-edited `?sharedParentStore=` naming this very store, a catalog
  // that never becomes usable (catalogId stays -1, or the catalog request fails),
  // and a resolve that hangs rather than errors. Each of those used to pin the
  // branch on the loader with no way back. The timer turns every one of them into
  // the promised silent fallback: drop the reference, clear the fragment, show the
  // plain menu. It is armed when the hold is set and cleared whenever it is
  // released, so on the normal path it never fires.
  private sharedCategoryHoldTimer: ReturnType<typeof setTimeout> | null = null;
  // Story #11520 - fired by the watchdog above so giving up is TERMINAL. The
  // watchdog is armed at hydration and the rxjs timeout() only starts at subscribe,
  // which is strictly later, so the watchdog deadline always expires first. Without
  // this the resolve subscription stayed live after the watchdog had already put the
  // plain menu back, and an answer landing in the gap between the two deadlines
  // yanked a customer who had been browsing that menu onto a category they no longer
  // expected - the opposite of the silent fallback the story promises.
  private readonly sharedCategoryResolveCancel$ = new Subject<void>();
  // Story #11520 - the ceiling on both the watchdog above and the resolve request
  // itself, so a slow or unreachable backend (TC-23) degrades to the plain menu
  // instead of an indefinite spinner. Comfortably longer than the resolve, which is
  // a single indexed lookup, and short enough to stay under the customer's patience.
  private static readonly SHARED_CATEGORY_HOLD_TIMEOUT_MS = 8000;
  private lazyStoreService: StoreService | null = null;
  // The NgRx selected store as of its latest emission: set from LoadStoreSuccess,
  // an id-less placeholder while a store loads (check `?.id`), and NOT gated on
  // the catalog the way `selectedStore` is. The 8s watchdog reads it with `latestPendingSharedItem` so it can leave a
  // mid-tier selector's re-held reference alone (Rigas #11520 round 7), and the
  // bar pages in `loadView` read it so they open as soon as the store is known
  // (story #11856).
  private latestSelectedStore: ClientStore | null = null;
  private latestPendingSharedItem: PendingSharedItemReference | null = null;

  constructor(
    private store: Store<ViewState>,
    private router: Router,
    private route: ActivatedRoute,
    private windowRefSer: WindowRefService,
    private location: Location,
    private whitelabelService: WhitelabelService,
    private cookieService: CookieService,
    private injector: Injector,
    private helper: HelperService,
    private visitCookies: VisitCookieService,
  ) {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.restoredNavigationId = event.restoredState?.navigationId ?? null;
      } else if (event instanceof NavigationEnd) {
        this.recordNavigationDepth();
      }
    });

    // Ahead of the catalog-gated subscription below, so a fragment it replays in
    // the same emission already sees the store (story #11856).
    this.store
      .select(getSelectedStore)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedStore) => {
        this.latestSelectedStore = selectedStore ?? null;
      });

    combineLatest([this.store.select(getSelectedStore), this.route.queryParams])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([selectedStore, queryParams]) => {
        if (selectedStore?.id) {
          this.setBasketEnabled(queryParams.basket === 'false' ? false : null);
          // Update cache when store changes
          if (selectedStore.aliasName !== this.lastStoreAliasName) {
            this.invalidateCache();
            this.lastStoreAliasName = selectedStore.aliasName;
            this.updateCookieCachedValues();
          }
          // Story #10478 - hydrate a pending shared item reference carried into a
          // branch via ?sharedParentStore=&sharedParentOffer= (cross-subdomain
          // navigation from the parent selector, where sessionStorage does not
          // carry over). Only when the current store is not the parent itself.
          this.hydratePendingSharedItemFromQueryParams(
            selectedStore,
            queryParams,
          );
        }
      });

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getSelectedStoreCatalog),
      this.store.select(getCurrentCartState),
      this.store.select(getStoreLocationsState),
      this.store.select(getSelectedLang),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([selectedStore, catalog, cartState, storeLocation, selectedLang]) => {
          if (!storeLocation || !storeLocation?.isValid) {
            this.storeLocation = null;
          }

          // `catalog` is null until the catalog slice loads, and this
          // subscription runs on every state emission — including the ones
          // where a store is selected but the catalog is not in yet. Reading
          // `catalog.data` unguarded throws there, and because the throw
          // happens inside a long-lived subscription it surfaces detached from
          // whatever triggered it.
          if (selectedStore && catalog?.data && catalog.data.catalogId !== -1) {
            this.selectedStore = selectedStore;
            if (!this.storeLocationUrl && this.getLocationLabel()) {
              this.storeLocationUrl = this.getLocationLabel();

              if (
                this.selectedStore?.id &&
                this.getLocationType() == 'url-location'
              ) {
                this.store.dispatch(
                  new ValidateStoreLocations(
                    this.selectedStore.id,
                    this.getLocationLabel(),
                  ),
                );
              }
            }
            this.catalog = catalog;
            if (cartState) {
              this.cart = cartState;
            }
            if (
              storeLocation &&
              storeLocation.isValid &&
              storeLocation.id !== -1
            ) {
              this.storeLocation = storeLocation.label;
              if (this.refreshActionAfterLocationValidation) {
                this.loadView(this.refreshActionAfterLocationValidation);
                // 04.06.2020: don't persist the location to the current order
                // https://trello.com/c/dOfSiejV/1640-order-with-location-improvments
                this.refreshActionAfterLocationValidation = null;
              }
            }
            if (this.refreshAction) {
              // Consume before calling: loadView may legitimately re-defer
              // (set refreshAction again) when the store is still hydrating,
              // and nulling afterwards would wipe that out. Also reset the
              // same-path dedupe (#10405): a deferred render replays the exact
              // path loadView just recorded, and when the catalog loads within
              // 100ms the dedupe would swallow the replay, stranding the view
              // on the loader (intermittent parent deep-link stall, #10478).
              const deferredPath = this.refreshAction;
              this.refreshAction = null;
              this.lastLoadViewPath = null;
              this.loadView(deferredPath);
            }
          }
          if (selectedLang) {
            this.selectedLang = selectedLang;
          }
        },
      );
    // Story #10478 - once the chosen branch (a store other than the parent that
    // held the shared reference) has its catalog loaded, resolve the parent's
    // shared offer to the branch's own offer and redirect so the item page opens
    // first. Silent fallback (no toast) when the branch does not carry the item.
    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getSelectedStoreCatalog),
      this.store.select(getPendingSharedItem),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([selectedStore, catalog, pending]) => {
        this.latestPendingSharedItem = pending ?? null;
        this.maybeResolvePendingSharedItem(selectedStore, catalog, pending);
      });

    this.store
      .select(getCurrentError)
      .pipe(
        withLatestFrom(this.store.select(getCurrentStoreViewStatus)),
        takeUntil(this.destroy$),
      )
      .subscribe(([state, viewStatus]) => {
        if (state && state.status !== 'INITIAL') {
          // Card #11603 - only switch to the error page when it is not already
          // showing. A fresh ERRORPAGE view object on every error re-ran every
          // subscriber of the view state, including the start-up orchestration
          // that had thrown, which is what turned one fault into a burst.
          if (viewStatus?.state !== 'ERRORPAGE') {
            this.store.dispatch(
              new ViewStateUpdate(
                {
                  state: 'ERRORPAGE',
                },
                {
                  src: 'LocationService',
                  description: 'Error has occured',
                },
              ),
            );
          }
        }
      });

    this.store
      .select(getStoreLocationsState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((storeLocation) => {
        if (storeLocation && !storeLocation.isValid) {
          const currentUrl = window.location.href;
          // Only react to an invalid location when the URL actually carries a
          // `#l/<location>` segment. Location-less / terminal views (e.g.
          // `#thankyou`, `#paymentProgress`) legitimately have no location, so
          // clearing the pending view render (`refreshAction`) here would
          // strand that render and leave the Thank You page stuck on the
          // loader (card #10584).
          if (LocationService.LOCATION_HASH_SEGMENT.test(currentUrl)) {
            this.refreshAction = null;
            this.refreshActionAfterLocationValidation = null;
            const newUrl = currentUrl.replace(
              LocationService.LOCATION_HASH_SEGMENT,
              '#',
            );
            // Card #11603: WebKit throws SecurityError from pushState (rate
            // limit, cross-origin target), and this runs inside a long-lived
            // subscription in the start-up path, so an uncaught throw here
            // takes the whole menu down. Every sibling method in this file
            // already guards its history call; this one did not.
            try {
              history.pushState({}, '', newUrl);
            } catch {
              // Cosmetic URL tidy-up only — the invalid location has already
              // been cleared from state, so failing to rewrite the address bar
              // must not interrupt the render.
            }
          }
        }
      });
  }
  ngOnDestroy() {
    // Story #11520 - the watchdog is a bare setTimeout, so takeUntil does not reach
    // it; leaving it armed would dispatch into a torn-down service.
    this.clearSharedCategoryHoldTimer();
    this.sharedCategoryResolveCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Story #10478 - reads the shared item reference carried in the branch URL query
   * params and, when present and this store is the branch (not the parent), stores
   * it as the pending reference so the resolve+redirect subscription can fire.
   *
   * Story #11520 - the reference is either an offer or a category, never both:
   * `?sharedParentOffer=` and `?sharedParentCategory=` are mutually exclusive and a
   * URL carrying both is ignored, since a link only ever carries one fragment.
   */
  private hydratePendingSharedItemFromQueryParams(
    selectedStore: ClientStore,
    queryParams: { [key: string]: any },
  ): void {
    const parentStoreId = parseInt(queryParams?.sharedParentStore, 10);
    const parentOfferId = parseInt(queryParams?.sharedParentOffer, 10);
    const parentCategoryId = parseInt(queryParams?.sharedParentCategory, 10);
    const hasOffer = Number.isFinite(parentOfferId) && parentOfferId > 0;
    const hasCategory =
      Number.isFinite(parentCategoryId) && parentCategoryId > 0;
    if (!Number.isFinite(parentStoreId) || parentStoreId <= 0) {
      return;
    }
    if (hasOffer === hasCategory) {
      // Neither kind is hydrated here, for two different shapes.
      //
      // TC-21 - a URL carrying BOTH kinds is a tampered link (a real link only ever
      // carries one), and its parameters must still leave the address bar: otherwise
      // it stays reshareable and this branch re-evaluates on every queryParams
      // emission. Only the params are touched - no reference is stored and the view
      // is left exactly as it is, so the customer sees the plain menu.
      //
      // A `sharedParentStore` with NEITHER kind beside it is left alone on purpose.
      // It is the same shape story #10478's TC-08 pins for the item path ("returns
      // early for every invalid shared-item param shape", no navigation), so
      // stripping here would change shipped behaviour to tidy up a parameter nothing
      // reads: hydration is skipped either way and the leftover `sharedParentStore`
      // drives nothing on its own. The re-evaluation it costs is this comparison.
      if (hasOffer && hasCategory) {
        this.stripSharedReferenceQueryParams();
      }
      return;
    }
    const referenceId = hasOffer ? parentOfferId : parentCategoryId;
    const kind = hasOffer ? 'offer' : 'category';
    // Guard against re-dispatching for the same reference on this store.
    const hydrationKey = `${selectedStore.id}:${kind}:${parentStoreId}:${referenceId}`;
    if (this.hydratedSharedItemKey === hydrationKey) {
      return;
    }
    this.hydratedSharedItemKey = hydrationKey;
    this.store.dispatch(
      hasOffer
        ? new SetPendingSharedItem(parentStoreId, parentOfferId)
        : new SetPendingSharedCategory(parentStoreId, parentCategoryId),
    );
    // Strip the one-time params from the URL so a later refresh or reshare of
    // the branch URL does not re-trigger the resolve.
    const urlTree = this.router.parseUrl(this.router.url);
    this.deleteSharedReferenceQueryParams(urlTree);
    // Story #11691 / #11520 - a store that only passes the reference on shows
    // its own branch list, so it gets neither the fragment nor the loader below.
    if (this.passesSharedItemOn(selectedStore, parentStoreId)) {
      this.router.navigateByUrl(urlTree, { replaceUrl: true });
      return;
    }
    // Card #11081 - set the offer fragment in the same replaceState, and hold the
    // view on LOADING until the resolve answers.
    //
    // The fragment is NOT what suppresses the catalog render at this point:
    // LOADED is already the live view state by the time this runs (the boot
    // Scroll fires loadView(null) -> default case long before LoadStoreSuccess
    // populates selectedStore.id and lets this method run). What it does is stop
    // the NEXT default-case loadView (e.g. a stray Scroll / loadProperView) from
    // dispatching LOADED over the item, and keep the item page across a refresh.
    //
    // The LOADING dispatch is what removes the catalog flash: it replaces the
    // catalog render with the loader until maybeResolvePendingSharedItem knows
    // which offer id this branch actually stocks. We deliberately do NOT render
    // `offer/${parentOfferId}` here - that id is only valid when the branch
    // inherits the parent catalog, and rendering it blind would fire the offer
    // endpoint with a foreign id on copied catalogs (404 -> /not-found via
    // ErrorInterceptor), breaking story #10478's silent fallback.
    //
    // Story #11520 - a shared CATEGORY takes the same shape: the fragment is
    // `#category/{parentCategoryId}`, which is safe to write blind (unlike an offer
    // id it fires no lookup - an id this branch does not have just leaves the menu
    // on its first category), and the loader is held the same way.
    const fragmentPath = hasOffer
      ? `offer/${parentOfferId}`
      : `category/${parentCategoryId}`;
    urlTree.fragment = fragmentPath;
    // #11242 - use navigateByUrl so router.url is updated together with the
    // browser address bar (replaceState bypasses Angular Router's state).
    this.hydrationEchoAnchor = fragmentPath;
    this.hydrationEchoTime = Date.now();
    if (hasCategory) {
      this.holdViewForSharedCategory();
    }
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
    this.store.dispatch(
      new ViewStateUpdate(
        { state: 'LOADING' },
        {
          src: 'LocationService',
          description: hasOffer
            ? 'Shared item pending resolve, hold the catalog render'
            : 'Shared category pending resolve, hold the catalog render',
        },
      ),
    );
  }

  /**
   * Card #11081 - drops a `#offer/{id}` fragment left behind by the shared-item
   * hydration. Without this the default-case guard in `loadView` keeps
   * suppressing the only LOADED dispatch reachable on a branch, so the catalog
   * home can never come back once the resolve declines.
   *
   * Story #11520 - a declined shared CATEGORY leaves a `#category/{parentCategoryId}`
   * fragment naming an id this branch does not have, which would survive a refresh
   * and a reshare; it is dropped here for the same reason.
   */
  private clearSharedReferenceFragment(): void {
    const hash = this.windowRefSer.nativeWindowLocation.hash;
    if (hash.includes('#offer') || hash.includes('#category')) {
      // replaceFragment, not `hash = ''`: the assignment would push a history
      // entry and leave the stale #offer/{parentOfferId} URL reachable via Back.
      this.replaceFragment('');
    }
  }

  /**
   * Story #11520 - drops the three one-time shared-reference parameters from a URL
   * tree. Shared by the hydration (which strips them as it stores the reference)
   * and by the ambiguous-URL guard (which strips them and stores nothing).
   */
  private deleteSharedReferenceQueryParams(urlTree: UrlTree): void {
    delete urlTree.queryParams['sharedParentStore'];
    delete urlTree.queryParams['sharedParentOffer'];
    delete urlTree.queryParams['sharedParentCategory'];
  }

  /**
   * Story #11520, TC-21 - takes the shared-reference parameters out of the address
   * bar without touching the fragment or the view state. replaceUrl, so the
   * tampered URL is not left behind a Back press.
   */
  private stripSharedReferenceQueryParams(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    this.deleteSharedReferenceQueryParams(urlTree);
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  /**
   * Story #11520 - holds the branch on the loader until the shared-category resolve
   * answers, with a watchdog so the hold can never outlive the attempt. See the
   * field comment on `sharedCategoryHoldTimer` for the three paths that reach the
   * timer instead of the resolve.
   */
  private holdViewForSharedCategory(): void {
    this.holdingViewForSharedCategory = true;
    this.clearSharedCategoryHoldTimer();
    this.sharedCategoryHoldTimer = setTimeout(() => {
      this.sharedCategoryHoldTimer = null;
      if (!this.holdingViewForSharedCategory) {
        return;
      }
      // Giving up has to be terminal: cancel any resolve still in flight BEFORE
      // restoring the menu, so a late answer cannot re-render the branch on a
      // category the customer has already been shown the plain menu instead of.
      this.sharedCategoryResolveCancel$.next();
      this.releaseSharedCategoryHold();
      // Mid-tier selector (parent above + children below): the reference was
      // re-held as SetPending*(M, X) for the leaf pick. Clearing it here would
      // drop the link while the customer is still reading M's branch list
      // (often >8s). Keep pending + fragment; only drop the loader hold.
      if (
        this.isLiveSelectorHeldReference(
          this.latestSelectedStore,
          this.latestPendingSharedItem,
        )
      ) {
        this.restoreCatalogView(
          'Shared category hold timed out on selector-mode parent',
        );
        return;
      }
      this.store.dispatch(new ClearPendingSharedItem());
      this.clearSharedReferenceFragment();
      this.restoreCatalogView('Shared category resolve did not answer in time');
    }, LocationService.SHARED_CATEGORY_HOLD_TIMEOUT_MS);
  }

  /**
   * Story #11520 (Rigas round 7) - pending is already keyed to this store and
   * the store shows a branch selector, so the link must stay until a leaf is
   * picked. Neither the 8s watchdog nor the self-parent early return may Clear.
   */
  private isLiveSelectorHeldReference(
    store: ClientStore | null | undefined,
    pending: PendingSharedItemReference | null | undefined,
  ): boolean {
    if (!store?.id || !pending || !sharedReferenceKind(pending)) {
      return false;
    }
    return (
      store.id === pending.parentStoreId && this.isSelectorModeParent(store)
    );
  }

  /** Story #11520 - the branch may render again; the watchdog is no longer needed. */
  private releaseSharedCategoryHold(): void {
    this.holdingViewForSharedCategory = false;
    this.clearSharedCategoryHoldTimer();
  }

  private clearSharedCategoryHoldTimer(): void {
    if (this.sharedCategoryHoldTimer !== null) {
      clearTimeout(this.sharedCategoryHoldTimer);
      this.sharedCategoryHoldTimer = null;
    }
  }

  /**
   * Rewrites the URL fragment on the CURRENT history entry, where a hash
   * assignment would push a new one. The target is built from pathname+search
   * so it differs from the current URL only in the fragment - a bare '#...'
   * argument resolves against the document base URI, which can turn this into
   * a cross-document navigation (a full reload).
   */
  private replaceFragment(fragment: string): void {
    const loc = this.windowRefSer.nativeWindowLocation;
    loc.replace(`${loc.pathname}${loc.search}#${fragment}`);
  }

  /**
   * Card #11081 - puts the branch back on the catalog home after the shared-item
   * resolve declines or fails, undoing the LOADING state the hydration set.
   * Story #10478's contract is a silent fallback to the branch catalog, so this
   * must never surface an error state.
   */
  private restoreCatalogView(description: string): void {
    this.store.dispatch(
      new ViewStateUpdate(
        { state: 'LOADED' },
        {
          src: 'LocationService',
          description,
        },
      ),
    );
  }

  /**
   * Story #10478 / #11520 - true when the store is a multi-store parent whose home
   * page is a selector (child list or delivery/pickup branch picker), i.e. it must
   * never show item/category detail from a shared deep link.
   *
   * A CURRENT-mode parent (or realtime-confirmation marketplace) shows its own
   * catalog and is treated like a normal store.
   *
   * Card #11375 - must agree with StoreDashboardComponent (`hasChild` from
   * `relation.childStores.length`, default CHILD_LIST_SELECTION when children
   * exist but no setting). An orphan selector setting with zero children must
   * open item/category detail like a normal store.
   *
   * Mid-tier brands (parent above + children below) hold pending shared
   * category/offer links for pass-through (#11691) because they have
   * childCount > 0. Pure leaves are excluded by the same childCount gate.
   */
  private isSelectorModeParent(store: ClientStore): boolean {
    if (store?.settings?.REQUIRES_REALTIME_CONFIRMATION) {
      return false;
    }
    // Story #11691 - a store with a parent is no longer excluded here. The
    // exclusion kept a branch that inherits PARENT_STORE_HOME_PAGE from
    // swallowing its own offer deep-link, and the childStores check below already
    // does that (#11375). The only store it still caught was the middle one of
    // organization > store > sales channel, which StoreDashboardComponent does
    // show as a branch list, so the item opened there instead of in a channel.
    // Card #11375 - settings and relation arrive together on LoadStoreSuccess;
    // a store that reaches this line with no childStores genuinely has none.
    if ((store?.relation?.childStores?.length ?? 0) === 0) {
      return false;
    }
    // Children exist: an explicit setting decides, and no setting defaults to
    // CHILD_LIST_SELECTION (same default as StoreDashboardComponent).
    return store.settings?.PARENT_STORE_HOME_PAGE !== 'CURRENT';
  }

  /**
   * Story #11520 - a cheap "is this store parent-shaped at all?" test, so the
   * selector-mode gate only ever runs for a store that could own branches. Every
   * ordinary store - which is what the category path serves on almost every call -
   * goes straight to selecting the category, exactly as before this story.
   *
   * Mid-tier parents (have a parent above them and children below) count; pure
   * leaf branches do not.
   */
  private mayBeSelectorModeParent(store: ClientStore): boolean {
    const homePage = store?.settings?.PARENT_STORE_HOME_PAGE;
    const childCount = store?.relation?.childStores?.length ?? 0;
    if (store?.relation?.parentStore && childCount === 0) {
      return false;
    }
    return (!!homePage && homePage !== 'CURRENT') || childCount > 0;
  }

  /**
   * Story #11691 / #11520 - true when the store sits between the store that
   * shared the item or category and the sales channel that will open it
   * (organization > store > sales channel): its parent shared the reference and
   * its own home page is a branch list. It keeps the reference for that list to
   * carry on, and resolves nothing.
   *
   * Only the direct parent counts, so a branch list in another chain still
   * resolves the reference and drops it there, as before.
   */
  private passesSharedItemOn(
    store: ClientStore,
    sharerStoreId: number,
  ): boolean {
    return (
      store?.relation?.parentStore?.storeId === sharerStoreId &&
      this.isSelectorModeParent(store)
    );
  }

  /**
   * Card #11375 / #11691 - settings and relation arrive together on
   * LoadStoreSuccess; there is no settings-before-relation race. Kept as a
   * no-op completeness check for call sites that still gate on it.
   */
  private shouldDeferSelectorModeOfferDeepLink(store: ClientStore): boolean {
    if (store?.settings?.REQUIRES_REALTIME_CONFIRMATION) {
      return false;
    }
    return store.settings === undefined;
  }

  /**
   * Story #10478 - lazily resolves StoreService via the Injector. StoreService
   * itself depends on LocationService, so a constructor injection here would form a
   * DI cycle; a lazy `injector.get` at call time breaks it.
   */
  private getStoreService(): StoreService {
    if (!this.lazyStoreService) {
      this.lazyStoreService = this.injector.get(StoreService);
    }
    return this.lazyStoreService;
  }

  /**
   * Story #10478 / #11520 - resolve the parent's shared reference to the chosen
   * branch's own offer or category and render it. Only runs once per
   * (branch, kind, reference); silent fallback when the branch does not carry it.
   */
  private maybeResolvePendingSharedItem(
    selectedStore: ClientStore,
    catalog: CatalogList,
    pending: PendingSharedItemReference | null,
  ): void {
    const kind = sharedReferenceKind(pending);
    if (
      !kind ||
      !selectedStore?.id ||
      !catalog ||
      catalog.data.catalogId === -1
    ) {
      return;
    }
    // Still on the parent that holds the reference - the selector is showing; wait
    // until the customer is routed into a branch (a different store).
    if (selectedStore.id === pending.parentStoreId) {
      // Story #11520 - release any loader hold. On a selector-mode parent (incl.
      // mid-tier) the list is the product: keep pending + fragment for the leaf
      // pick (Rigas #11520 round 7). On a non-selector (TC-16 hand-edited self
      // id) nothing can consume the link — drop fragment and Clear below.
      if (this.holdingViewForSharedCategory) {
        this.releaseSharedCategoryHold();
        if (!this.isSelectorModeParent(selectedStore)) {
          this.clearSharedReferenceFragment();
          this.restoreCatalogView(
            'Shared category names this store as its parent',
          );
        } else {
          this.restoreCatalogView(
            'Shared category held on selector-mode parent',
          );
        }
      }
      // TC-22 - a reference held on a store that is NOT a selector-mode parent can
      // never be consumed: no branch pick is coming, so drop it. Card #11375 /
      // #11691: settings and relation arrive together; wait for both before
      // deciding. Selector-mode (org or mid-tier pass-through): keep pending so
      // the leaf URL can still carry sharedParent*.
      if (
        selectedStore.settings !== undefined &&
        selectedStore.relation !== undefined &&
        !this.isSelectorModeParent(selectedStore)
      ) {
        this.store.dispatch(new ClearPendingSharedItem());
      }
      return;
    }
    // Story #11691 / #11520 - mid-tier pass-through: keep the sharer's reference
    // for the branch list to carry on; the sales channel resolves it.
    if (this.passesSharedItemOn(selectedStore, pending.parentStoreId)) {
      return;
    }
    const referenceId =
      kind === 'offer' ? pending.parentOfferId : pending.parentCategoryId;
    const resolveKey = `${selectedStore.id}:${kind}:${pending.parentStoreId}:${referenceId}`;
    if (this.resolvingSharedItemKey === resolveKey) {
      return;
    }
    this.resolvingSharedItemKey = resolveKey;

    if (kind === 'category') {
      this.resolvePendingSharedCategory(selectedStore, catalog, pending);
      return;
    }

    // Card #11081 - when the branch inherits the parent catalog the parent's
    // offer id IS a valid id here, so the item can open immediately instead of
    // waiting out the resolve round-trip. Gated on the offer actually being in the
    // loaded catalog, so a copied/independent catalog never fires the offer
    // endpoint with a foreign id.
    const alreadyStocked = (catalog.data?.offers ?? []).some(
      (offer) => offer.offerId === pending.parentOfferId,
    );
    let renderedOfferId: number | null = null;
    if (alreadyStocked) {
      renderedOfferId = pending.parentOfferId;
      // Clear before loadView so a later combineLatest cannot re-resolve the
      // same pending. Mid-tier never reaches here (passesSharedItemOn returns).
      this.store.dispatch(new ClearPendingSharedItem());
      this.loadView(`offer/${pending.parentOfferId}`);
    }

    this.getStoreService()
      .resolveParentOffer(
        selectedStore.id,
        pending.parentOfferId,
        pending.parentStoreId,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Eager path already cleared above; clear here only when resolve alone
          // consumed the reference.
          if (renderedOfferId === null) {
            this.store.dispatch(new ClearPendingSharedItem());
          }
          if (response?.carried && response.offerId) {
            // Card #11081 - the eager render above may already be showing this
            // exact offer (inherited catalog, where resolve returns the same id).
            // Re-rendering it would blank the open item page and rebuild it once
            // the offer GET returns, discarding any option/variant selections
            // made meanwhile, and would duplicate that GET and the view-item
            // tracking event.
            if (renderedOfferId === response.offerId) {
              return;
            }
            // Redirect into the branch item so the existing #offer flow opens the
            // item page first (before catalog home / basket). No toast on fallback.
            // The hash must be set too: loadView's default case dispatches LOADED
            // whenever the hash carries no '#offer', so a later default-case call
            // (e.g. a stray Scroll/loadProperView) would hide the item a moment
            // after it opened. With the hash in place that guard protects us, and
            // a refresh keeps the item page.
            //
            // replace(), not a hash assignment: assigning a different hash pushes
            // a history entry, and the entry behind it still carries the PARENT's
            // #offer/{id} from the hydration - an id this branch does not stock
            // on a copied catalog. Back from the item page then reopened that
            // stale URL. Replacing keeps this branch's id on the same entry, so
            // there is nothing wrong behind us.
            this.replaceFragment(`offer/${response.offerId}`);
            this.loadView(`offer/${response.offerId}`);
            return;
          }
          // Not carried by this branch. Card #11081 - the hydration left a
          // `#offer/{parentOfferId}` fragment behind; drop it and render the
          // catalog home, otherwise the default-case '#offer' guard suppresses
          // every later LOADED dispatch and the customer is stranded on the
          // loader with no way back to the menu. Skipped when the eager render
          // already opened a genuinely stocked item - that page stays.
          if (renderedOfferId === null) {
            this.clearSharedReferenceFragment();
            this.restoreCatalogView('Shared item not carried by this branch');
          }
        },
        error: () => {
          // Silent fallback (story #10478): drop the reference and land on catalog
          // home. Skip clear when eager already ran.
          if (renderedOfferId === null) {
            this.store.dispatch(new ClearPendingSharedItem());
            this.clearSharedReferenceFragment();
            this.restoreCatalogView('Shared item resolve failed');
          }
        },
      });
  }

  /**
   * Story #11520 - the category twin of the offer resolve above: map the parent's
   * shared category to this branch's own category and land the menu on it.
   *
   * On an inherited catalog the parent's id is already valid here, so the category
   * is selected immediately rather than after the round-trip, and a resolve that
   * comes back with that same id does not re-render. When the branch does not carry
   * the category - or the resolve fails, including a 404 from an older backend that
   * has no such endpoint - the fragment is dropped and the normal menu is restored,
   * with no toast and no message: indistinguishable from opening the branch with no
   * link at all.
   */
  private resolvePendingSharedCategory(
    selectedStore: ClientStore,
    catalog: CatalogList,
    pending: PendingSharedItemReference,
  ): void {
    const parentCategoryId = pending.parentCategoryId;
    const alreadyInCatalog =
      (catalog.data?.categories ?? []).some(
        (category) => category.categoryId === parentCategoryId,
      ) ||
      (catalog.data?.topLevelCategories ?? []).some(
        (category) => category.categoryId === parentCategoryId,
      );
    let renderedCategoryId: number | null = null;
    if (alreadyInCatalog) {
      renderedCategoryId = parentCategoryId;
      this.releaseSharedCategoryHold();
      // The hydration wrote this exact fragment moments ago, so its Scroll echo
      // may still be armed. This render is the intentional one, so consume the
      // one-shot guard rather than let it swallow the only render that selects
      // the category: the resolve then answers with the same id and returns
      // early, leaving the customer at the menu top.
      const categoryPath = `category/${parentCategoryId}`;
      if (this.hydrationEchoAnchor === categoryPath) {
        this.hydrationEchoAnchor = null;
      }
      // Clear before loadView so a later combineLatest cannot re-resolve the
      // same pending. Mid-tier never reaches here (passesSharedItemOn returns).
      this.store.dispatch(new ClearPendingSharedItem());
      this.loadView(categoryPath);
    }

    this.getStoreService()
      .resolveParentCategory(
        selectedStore.id,
        parentCategoryId,
        pending.parentStoreId,
      )
      // TC-23 - a request that HANGS never reaches the error handler below, so
      // without this the silent fallback the story promises would never run. The
      // timeout turns "slow or unreachable backend" into the same silent fallback a
      // 404 from an older backend already gets.
      // The watchdog cancel comes first in the chain so that the moment it fires
      // this subscription is gone: next/error below can no longer run, which is what
      // makes "the watchdog gave up" a state the resolve cannot come back from.
      .pipe(
        timeout(LocationService.SHARED_CATEGORY_HOLD_TIMEOUT_MS),
        takeUntil(this.sharedCategoryResolveCancel$),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.releaseSharedCategoryHold();
          // Eager path already cleared above; clear here only when resolve alone
          // consumed the reference.
          if (renderedCategoryId === null) {
            this.store.dispatch(new ClearPendingSharedItem());
          }
          if (response?.carried && response.categoryId) {
            // The eager render above may already be showing this exact category
            // (inherited catalog, where the resolve returns the same id).
            if (renderedCategoryId === response.categoryId) {
              return;
            }
            // replace(), not a hash assignment: assigning a different hash pushes a
            // history entry whose predecessor still carries the PARENT's category id,
            // so Back would reopen a URL that means nothing on this branch.
            this.replaceFragment(`category/${response.categoryId}`);
            this.loadView(`category/${response.categoryId}`);
            // loadView positions a page that mounts from here on. A page that a
            // stray LOADED already mounted is asked to scroll, which nothing
            // outside it could do before this story.
            this.store.dispatch(new ScrollToCategory(response.categoryId));
            return;
          }
          if (renderedCategoryId === null) {
            this.clearSharedReferenceFragment();
            this.restoreCatalogView(
              'Shared category not carried by this branch',
            );
          }
        },
        error: () => {
          this.releaseSharedCategoryHold();
          if (renderedCategoryId === null) {
            this.store.dispatch(new ClearPendingSharedItem());
            this.clearSharedReferenceFragment();
            this.restoreCatalogView('Shared category resolve failed');
          }
        },
      });
  }

  /**
   * Invalidates the cookie cache, forcing a refresh on next access
   * @private
   */
  private invalidateCache(): void {
    this.isCacheValid = false;
    this.cachedLocationLabel = null;
    this.cachedLocationType = null;
    this.cachedLocationOrder = null;

    this.isPrinterCacheValid = false;
    this.cachedPrinterName = null;
    this.cachedPrinterStoreId = null;
  }

  setPrinterName(storeId: number, printerName: string): void {
    if (!storeId || !printerName) {
      return;
    }
    // Persist for a week (same as table/room style persistence)
    this.cookieService.set(`printer-name-${storeId}`, printerName, 7);
    this.invalidateCache();
  }

  getPrinterName(storeId: number): string | null {
    if (!storeId) {
      return null;
    }

    if (this.isPrinterCacheValid && this.cachedPrinterStoreId === storeId) {
      return this.cachedPrinterName;
    }

    const value = this.cookieService.get(`printer-name-${storeId}`);
    this.cachedPrinterName = value || null;
    this.cachedPrinterStoreId = storeId;
    this.isPrinterCacheValid = true;
    return this.cachedPrinterName;
  }

  // #11435 Like `?salespoint=`, the printer may sit before the `#` (`/?printer=X`)
  // or inside the hash when it is appended to a table link (`/#l/1?printer=X`).
  // Reading only `location.search` lost the printer on every table link.
  readPrinterFromUrl(): string | null {
    const loc = this.windowRefSer?.nativeWindowLocation;
    return getQueryParamFromUrl(loc?.search, loc?.hash, 'printer');
  }

  removePrinterFromUrl(): void {
    try {
      const cleanUrl = removeQueryParamFromUrl(
        this.windowRefSer.nativeWindowLocation.href,
        'printer',
      );
      if (!cleanUrl) {
        return;
      }
      this.location.replaceState(cleanUrl);
    } catch {
      // no-op: do not break customer flow if URL parsing fails
    }
  }

  /**
   * Updates cached cookie values to prevent repeated cookie reads
   * Handles null/undefined cases properly to avoid unnecessary re-renders
   * @private
   */
  private updateCookieCachedValues(): void {
    if (!this.selectedStore?.aliasName) {
      this.invalidateCache();
      return;
    }

    const storeAlias = this.selectedStore.aliasName;

    // Check URL first for location label
    const urlLocationMatch = this.router.url.match(/\/#l\/([^/?#]+)/);
    const urlLocation = urlLocationMatch ? urlLocationMatch[1] : null;

    // Cache location label (URL takes precedence)
    if (urlLocation) {
      this.cachedLocationLabel = urlLocation;
    } else {
      const cookieLabel = this.visitCookies.get(
        `location-order-label-${storeAlias}`,
      );
      this.cachedLocationLabel = cookieLabel || null;
    }

    // Cache location type
    const cookieType = this.visitCookies.get(
      `location-order-type-${storeAlias}`,
    );
    this.cachedLocationType =
      (cookieType as 'url-location' | 'cart-location') || null;

    // Cache location order
    const cookieOrder = this.visitCookies.get(`location-order-${storeAlias}`);
    this.cachedLocationOrder = cookieOrder || null;

    this.isCacheValid = true;
  }

  /**
   * Ensures cache is valid before reading cached values
   * @private
   */
  private ensureCacheValid(): void {
    if (
      !this.isCacheValid ||
      this.lastStoreAliasName !== this.selectedStore?.aliasName
    ) {
      this.updateCookieCachedValues();
    }
  }

  /**
   * Bottom menu bar pages belong to the template apps (story #10267). Their
   * routes only exist in a Template build, and this guard covers the other way
   * in — a `#widget-…` fragment typed on a customer UI, kiosk or admin order
   * capture URL — so those apps can never enter a widget view.
   */
  private dispatchWidgetView(
    state: StoreViewState['state'],
    description: string,
  ): void {
    if (environment.mode !== APPMODE.Template) {
      return;
    }
    this.store.dispatch(
      new ViewStateUpdate({ state }, { src: 'LocationService', description }),
    );
  }

  loadView(path) {
    if (
      path &&
      path === this.hydrationEchoAnchor &&
      Date.now() - this.hydrationEchoTime <
        LocationService.HYDRATION_ECHO_WINDOW_MS
    ) {
      this.hydrationEchoAnchor = null;
      return;
    }

    // The 100ms same-path dedupe (#10405) must not suppress a render for a
    // terminal/confirmation view — those are reached once after payment and
    // store-loading may legitimately re-issue loadView for the same path as
    // the store finishes loading. Dropping that repeat strands the render and
    // leaves the Thank You page stuck on the loader (card #10584).
    const isTerminalView =
      typeof path === 'string' &&
      LocationService.TERMINAL_VIEW_KEYWORDS.some((keyword) =>
        path.includes(keyword),
      );
    const now = Date.now();
    if (
      !isTerminalView &&
      path === this.lastLoadViewPath &&
      now - this.lastLoadViewTime < 100
    ) {
      return;
    }
    this.lastLoadViewPath = path;
    this.lastLoadViewTime = now;

    let urlParams = [''];
    if (path) {
      urlParams = path.split('?')[0].split('/');
      while (urlParams.length) {
        if (urlParams[0] === 'orderUuid') {
          LocationService.orderUuid = urlParams[1];
        } else if (urlParams[0] === 'l') {
          this.storeLocationUrl = urlParams[1];
          if (this.selectedStore) {
            this.setLocationUrlType(
              this.selectedStore.aliasName,
              'url-location',
            );
            if (this.storeLocation) {
              if (this.storeLocation !== urlParams[1]) {
                if (this.selectedStore) {
                  this.refreshActionAfterLocationValidation = path;
                  this.store.dispatch(
                    new ValidateStoreLocations(
                      this.selectedStore.id,
                      urlParams[1],
                    ),
                  );
                }
                this.refreshAction = path;
              }
            } else {
              if (this.selectedStore) {
                this.refreshActionAfterLocationValidation = path;
                this.store.dispatch(
                  new ValidateStoreLocations(
                    this.selectedStore.id,
                    urlParams[1],
                  ),
                );
              }
              this.refreshAction = path;
            }
          }
        } else {
          break;
        }
        urlParams.splice(0, 2);
      }
    }

    if (
      !this.storeLocationUrl &&
      this.getLocationLabel() &&
      this.getLocationType() === 'url-location'
    ) {
      this.storeLocationUrl = this.getLocationLabel();
      if (this.selectedStore?.id) {
        this.store.dispatch(
          new ValidateStoreLocations(
            this.selectedStore.id,
            this.getLocationLabel(),
          ),
        );
      }
    }

    this.currentView = urlParams[0];

    switch (urlParams[0]) {
      case 'category': {
        const categoryId = parseInt(urlParams[1], 10);
        // Story #11520 - while a shared-category resolve is in flight the fragment
        // still names the PARENT's category id, which means nothing on this branch.
        // Rendering it would drop the loader for a menu at the top and then jump;
        // the resolve re-enters loadView with the branch's own id.
        if (this.holdingViewForSharedCategory) {
          break;
        }
        if (this.selectedStore && this.catalog) {
          // Story #11520 - a selector-mode parent must never render a category from
          // a shared link: hold the reference and let the selector render, and the
          // resolve+redirect (constructor subscription) lands the customer on the
          // branch's own category once a branch is chosen. A CURRENT-mode parent or
          // a normal store keeps today's behaviour and selects the category.
          //
          // Unlike the `offer` case this does NOT defer on missing settings. This
          // case is also the ordinary browsing path - the app writes
          // `#category/<id>` into the URL as the customer scrolls - so a deferral
          // that never resolves would strand the menu on the loader. Only a store
          // that could be a multi-store parent at all is taken through the gate,
          // and for such a store isSelectorModeParent() infers selector mode from
          // the child stores when PARENT_STORE_HOME_PAGE has not arrived.
          //
          // The scroll-sync fragment cannot reach this gate, which is what makes it
          // safe to decide from the store shape rather than from "did this fragment
          // come from a shared link". Two independent reasons, either one enough:
          //
          // 1. A selector-mode parent never mounts the menu. StoreDashboardComponent
          //    renders <app-store-page> only when `!hasChild || homePage ===
          //    'CURRENT'` (store-dashboard.component.html), which is the exact
          //    complement of isSelectorModeParent(), so on a store this gate holds a
          //    reference for there is no store page to scroll, and
          //    updateUrlWithoutNavigating() - the only writer of the browsing
          //    `#category/<id>` - lives on that component. Pinned by
          //    store-dashboard.component.spec.ts.
          // 2. That writer uses Location.replaceState, which bypasses the Router, so
          //    it raises no Scroll event and loadView() is not re-entered at all.
          //    Only a real navigation (a deep link, a refresh, the refreshAction
          //    replay or the resolve's own redirect) gets here.
          if (this.mayBeSelectorModeParent(this.selectedStore)) {
            if (this.shouldDeferSelectorModeOfferDeepLink(this.selectedStore)) {
              this.refreshAction = path;
              break;
            }
            // TC-22 - `settings` not on the payload yet is NOT the same as "this
            // parent has no PARENT_STORE_HOME_PAGE". Inferring selector mode from
            // the child stores in that window holds the reference on a parent whose
            // home page turns out to be CURRENT, so the category is never selected
            // and a pending reference is written that nothing clears - it is then
            // carried into the next branch URL the customer builds and opens a
            // category they never asked for. Defer instead: settings arrive with
            // the store payload, and that emission replays `refreshAction`, so this
            // is bounded exactly like the `relation` deferral above.
            if (this.selectedStore.settings === undefined) {
              this.refreshAction = path;
              break;
            }
            if (
              Number.isFinite(categoryId) &&
              categoryId > 0 &&
              this.isSelectorModeParent(this.selectedStore)
            ) {
              this.store.dispatch(
                new SetPendingSharedCategory(this.selectedStore.id, categoryId),
              );
              // Selector list is the product — no resolve loader to hold (Rigas
              // #11520 round 7). Cancels the 8s watchdog if hydration armed it.
              if (this.holdingViewForSharedCategory) {
                this.releaseSharedCategoryHold();
              }
              this.store.dispatch(
                new ViewStateUpdate(
                  { state: 'LOADED' },
                  {
                    src: 'LocationService',
                    description:
                      'Load view, selector-mode parent category deep-link',
                  },
                ),
              );
              break;
            }
          }
          this.store.dispatch(
            new SelectCategory(categoryId, {
              src: 'LocationService',
              description: 'Load view, category',
            }),
          );
          this.store.dispatch(
            new ViewStateUpdate(
              { state: 'LOADED' },
              {
                src: 'LocationService',
                description: 'Load view, category',
              },
            ),
          );
        } else {
          this.refreshAction = path;
        }
        break;
      }
      case 'standalone-payment':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'STANDALONEPAYMENT' },
            {
              src: 'LocationService',
              description: 'Load view, standalone payment',
            },
          ),
        );
        break;
      case 'offer': {
        const offerId = parseInt(urlParams[1], 10);
        if (this.selectedStore && this.catalog) {
          // Story #10478 - defer when the store's settings haven't arrived yet.
          // isSelectorModeParent reads PARENT_STORE_HOME_PAGE from settings; without
          // it we'd wrongly take the offer path on the parent and flash item detail.
          // refreshAction is re-consumed on the next combineLatest emission.
          if (!this.selectedStore?.settings) {
            this.refreshAction = path;
            break;
          }
          if (offerId) {
            // Story #10478 - a selector-mode parent must never render item detail.
            // Hold the parent's shared offer reference and let the selector show;
            // the resolve+redirect (constructor subscription) opens the branch item
            // once a branch is chosen. A CURRENT-mode parent or a normal store keeps
            // today's behaviour and opens the item directly.
            if (this.isSelectorModeParent(this.selectedStore)) {
              this.store.dispatch(
                new SetPendingSharedItem(this.selectedStore.id, offerId),
              );
              // Selector list is the product — no resolve loader to hold.
              if (this.holdingViewForSharedCategory) {
                this.releaseSharedCategoryHold();
              }
              // Let the store render (shows the multi-store selector); the
              // pending item reference will be carried through once a branch is picked.
              this.store.dispatch(
                new ViewStateUpdate(
                  { state: 'LOADED' },
                  {
                    src: 'LocationService',
                    description:
                      'Load view, selector-mode parent offer deep-link',
                  },
                ),
              );
              break;
            }
            this.store.dispatch(
              new ViewOrderItem(
                this.selectedStore.id,
                this.catalog.data.catalogId,
                offerId,
                '',
                this.selectedLang,
              ),
            );
            this.store.dispatch(
              new ViewStateUpdate(
                { state: 'VIEWPRODUCTDETAILS' },
                {
                  src: 'LocationService',
                  description: 'Load view, offer',
                },
              ),
            );
          } else {
            this.store.dispatch(
              new ErrorMessage('public.global.errorExpected'),
            );
          }
        } else {
          this.refreshAction = path;
        }
        break;
      }
      case 'orderItem': {
        const orderId = urlParams[1];
        if (
          this.selectedStore &&
          this.catalog &&
          this.cart &&
          this.cart.orderItems
        ) {
          if (orderId) {
            let offerId = null;
            this.cart.orderItems.forEach((orderItem) => {
              if (orderItem.uuid === orderId) {
                offerId = orderItem.offerId;
              }
            });
            if (offerId) {
              this.store.dispatch(
                new ViewOrderItem(
                  this.selectedStore.id,
                  this.catalog.data.catalogId,
                  offerId,
                  orderId,
                  this.selectedLang,
                ),
              );
              this.store.dispatch(
                new ViewStateUpdate(
                  { state: 'VIEWPRODUCTDETAILSFROMCARTVIEW' },
                  {
                    src: 'LocationService',
                    description: 'Load view, order item',
                  },
                ),
              );
            } else {
              this.store.dispatch(
                new ErrorMessage('public.global.errorExpected'),
              );
              console.error('unexpected offerId value', offerId);
            }
          } else {
            this.store.dispatch(
              new ErrorMessage('public.global.errorExpected'),
            );
            console.error('unexpected orderUuid value', orderId);
          }
        } else {
          this.refreshAction = path;
        }
        break;
      }
      case 'cart':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'VIEWCART' },
            {
              src: 'LocationService',
              description: 'Load view, cart',
            },
          ),
        );
        break;
      case 'checkout-recommendations':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CHECKOUT_RECOMMENDATIONS' },
            {
              src: 'LocationService',
              description: 'Load view, checkout recommendations',
            },
          ),
        );
        break;
      case 'login':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'LOGIN' },
            {
              src: 'LocationService',
              description: 'Load view, login',
            },
          ),
        );
        break;
      case 'precheckout':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PRECHECKOUT' },
            {
              src: 'LocationService',
              description: 'Load view, precheckout',
            },
          ),
        );
        break;
      case 'profile':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PROFILE' },
            {
              src: 'LocationService',
              description: 'Load view, profile',
            },
          ),
        );
        break;
      case 'edit-profile':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'EDITPROFILE' },
            {
              src: 'LocationService',
              description: 'Load view, edit profile',
            },
          ),
        );
        break;
      case 'edit-address':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'EDITADDRESS' },
            {
              src: 'LocationService',
              description: 'Load view, edit address',
            },
          ),
        );
        break;
      case 'emptycart':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'EMPTYCARTPAGE' },
            {
              src: 'LocationService',
              description: 'Load view, empty cart page',
            },
          ),
        );
        break;
      case 'confirmation-failed':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CONFIRMATIONFAILEDPAGE' },
            {
              src: 'LocationService',
              description: 'Load view, confirmation failed page',
            },
          ),
        );
        break;
      case 'checkout':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CHECKOUT' },
            {
              src: 'LocationService',
              description: 'Load view, checkout',
            },
          ),
        );
        break;
      case 'thankyou':
        if (this.selectedStore) {
          if (urlParams[1]) {
            let orderId = urlParams[1];
            if (urlParams[1] === 'e') {
              LocationService.orderUuid = this.cart.uuid;
              orderId = urlParams[2];
            }
            this.store.dispatch(
              new ViewOrderStatus(
                this.selectedStore.id,
                orderId,
                'CHECKEXISTING',
                this.selectedLang,
              ),
            );
            this.store.dispatch(
              new ViewStateUpdate(
                { state: 'THANKYOUPAGE' },
                {
                  src: 'LocationService',
                  description: 'Load view',
                },
              ),
            );
          } else {
            this.store.dispatch(
              new ErrorMessage('public.global.errorExpected'),
            );
            console.error('unexpected orderUuid value', urlParams[1]);
          }
        } else {
          this.refreshAction = path;
        }
        break;
      case 'verifyPayment':
        const storeId = urlParams[1];
        const orderUuid = urlParams[2];
        const status = urlParams[3];
        let reqPayload = path.split('?')[1];
        reqPayload = JSON.parse(
          '{"' +
            decodeURI(reqPayload)
              .replace(/"/g, '\\"')
              .replace(/&/g, '","')
              .replace(/=/g, '":"') +
            '"}',
        );
        if (
          this.selectedStore &&
          storeId &&
          String(this.selectedStore.id) !== storeId
        ) {
          this.store.dispatch(new ErrorMessage('public.global.errorExpected'));
          return;
        }
        if (storeId && orderUuid && reqPayload) {
          const queryParams: TrustPaymentsPaymentInfo = reqPayload;
          if (
            !this.trustPaymentsTransactionReference ||
            this.trustPaymentsTransactionReference !==
              queryParams.transactionreference
          ) {
            this.trustPaymentsTransactionReference =
              queryParams.transactionreference;
            switch (status) {
              case 'error':
              case 'declined':
              case 'success':
                this.store.dispatch(
                  new CompleteTrustPaymentsPayment(
                    Number(storeId),
                    orderUuid,
                    queryParams,
                  ),
                );
                break;
            }
          }
        } else {
          this.store.dispatch(new ErrorMessage('public.global.errorExpected'));
          console.error(
            'TrustPayments payment failed for orderUuid value',
            orderUuid,
          );
          this.refreshAction = path;
        }
        break;
      case 'paymentError':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'ERRORPAGE' },
            {
              src: 'LocationService',
              description: 'Load view, payment error',
            },
          ),
        );
        break;
      case 'paymentProgress':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PAYMENTPROGRESS' },
            {
              src: 'LocationService',
              description: 'Load payment progress screen',
            },
          ),
        );
        break;
      case 'paymentProgressError':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PAYMENTPROGRESSERROR' },
            {
              src: 'LocationService',
              description: 'Load payment progress error screen',
            },
          ),
        );
        break;
      case 'paymentPay':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PAYMENTPAY' },
            {
              src: 'LocationService',
              description: 'Load payment pay screen',
            },
          ),
        );
        break;
      case 'qrCodePay':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'QRCODEPAY' },
            {
              src: 'LocationService',
              description: 'Load qr code pay screen',
            },
          ),
        );
        break;
      case 'require-location':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'REQUIREDLOCATION' },
            {
              src: 'LocationService',
              description: 'Load required location screen',
            },
          ),
        );
        break;
      case 'sales-point-required':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'SALESPOINTREQUIRED' },
            {
              src: 'LocationService',
              description: 'Load required Sales Point screen',
            },
          ),
        );
        break;
      case 'kiosk-video':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'KIOSKVIDEO' },
            {
              src: 'LocationService',
              description: 'Kiosk video screen',
            },
          ),
        );
        break;
      case 'consumption-mode':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'CONSUMPTION_MODE' },
            {
              src: 'LocationService',
              description: 'Eat-In / Takeaway',
            },
          ),
        );
        break;
      case 'promotion':
        this.store.dispatch(
          new ViewStateUpdate(
            { state: 'PROMOTION' },
            {
              src: 'LocationService',
              description: 'Promotion details screen',
            },
          ),
        );
        break;
      case 'widget-menu':
        this.dispatchWidgetView(
          'LOADED',
          'Widget menu page (shows store dashboard)',
        );
        break;
      case 'widget-stores':
        this.dispatchWidgetView('STORES', 'Widget stores page');
        break;
      case 'widget-loyalty-promotions':
        // Card #11440: a stray fragment (deep link, restored history, app
        // resume) must not open this widget page without a selected store —
        // the same rule the bottom-bar tab visibility applies one layer up.
        // Story #11856: judged on the NgRx store, which is known from
        // LoadStoreSuccess, not on `selectedStore`, which waits for the
        // catalog. Reading the latter dropped the start page of a template app
        // whose first tab is this page, and every tap made before the catalog
        // arrived. Nothing is parked: no store means no page, so a later replay
        // can never pull the customer off a page they chose since.
        if (
          isWidgetRouteAvailable('loyalty-promotions', this.latestSelectedStore)
        ) {
          this.dispatchWidgetView(
            'LOYALTY_PROMOTIONS',
            'Widget loyalty promotions page',
          );
        }
        break;
      case 'widget-member-code':
        if (isWidgetRouteAvailable('member-code', this.latestSelectedStore)) {
          this.dispatchWidgetView('MEMBER_CODE', 'Widget member code page');
        }
        break;
      case 'widget-brochure': {
        // Story #11626. Unlike the other bar pages this one also needs content
        // to exist, so "store not loaded yet" and "loaded, no brochure" end
        // differently. A cold app start, or a deep link to
        // /:storeAlias/brochure, can reach here before LoadStoreSuccess has
        // populated the store, and simply dropping the fragment would leave
        // the customer on the dashboard with no error and nothing to retry.
        //
        // So defer rather than drop, through the same `refreshAction` replay
        // the payment and orderUuid cases above use, until the store is known.
        // Like its siblings it is judged on the NgRx store (story #11856), not
        // on the catalog-gated `selectedStore`. Once the store is known its own
        // park is consumed, so the catalog-time replay can never pull the
        // customer back here from a page they moved to meanwhile. Only with
        // the store known does a missing brochure mean "stale fragment", and
        // then it is left on whatever view is showing.
        //
        // The template-mode check mirrors `dispatchWidgetView`'s own gate, so
        // a stray fragment in the web build stays inert instead of parking a
        // replay that could never render anything.
        if (environment.mode !== APPMODE.Template) {
          break;
        }
        const brochureStore = this.latestSelectedStore;
        if (!brochureStore?.id) {
          this.refreshAction = path;
          break;
        }
        if (this.refreshAction === path) {
          this.refreshAction = null;
        }
        if (isWidgetRouteAvailable('brochure', brochureStore)) {
          this.dispatchWidgetView('BROCHURE', 'Widget brochure page');
        }
        break;
      }
      default:
        const isThankYouPage = window.location.hash.includes('#thankyou');
        const isOfferPage = window.location.hash.includes('#offer');
        const isRecommendationsPage = window.location.hash.includes(
          '#checkout-recommendations',
        );
        if (
          !isThankYouPage &&
          !isOfferPage &&
          !isRecommendationsPage &&
          // Story #11520 - the branch is deliberately held on the loader until the
          // shared-category resolve answers; a stray default-case render here would
          // show the menu top and then jump.
          !this.holdingViewForSharedCategory
        ) {
          this.store.dispatch(
            new ViewStateUpdate(
              { state: 'LOADED' },
              {
                src: 'LocationService',
                description: 'Load view, default',
              },
            ),
          );
        }
        break;
    }
  }

  /**
   * get orderUuid passed through url
   * it will always take presedence over the cookie or state orderUuid
   * @returns (string) orderUuid
   */
  getOrderUuid() {
    return LocationService.orderUuid;
  }
  setOrderUuid(uuid: string) {
    LocationService.orderUuid = uuid;
  }

  /**
   * get bookingId passed through url
   * it will always take presedence over the cookie or state bookingId
   * @returns (string) bookingId
   */
  getEventBookingId() {
    return LocationService.bookingId;
  }
  setEventBookingId(bookingId: number) {
    LocationService.bookingId = bookingId;
  }

  /**
   * get storeLocation passed through url
   * it will always take presedence over the cookie or state storeLocation
   * @returns (string) storeLocation
   */
  getStoreLocation() {
    return this.storeLocation !== 'null' ? this.storeLocation : null;
  }
  getUrlStoreLocation() {
    return this.getLocationLabel() !== 'null' ? this.getLocationLabel() : null;
  }
  clearStoreLocationUrl() {
    this.removeLocationLabel(this.selectedStore?.aliasName);
  }
  setStoreLocation(location) {
    this.storeLocation = location;
  }

  public_url() {
    const url: string = this.router.url;
    return url.split('#')[0];
  }

  /**
   * Full-page redirect back to the customer UI base URL — e.g. sending the
   * customer to a fresh store homepage from the real-time confirmation failure
   * page (card #9601). Goes through WindowRefService rather than touching
   * `window.location` directly so the side-effect can be mocked in tests.
   */
  redirectToPublicUrl(): void {
    this.windowRefSer.nativeWindowLocation.href = this.public_url();
  }
  /**
   * returns customer UI base navigation url
   * it determines if orderUuid and location params should be prepended to the hash
   * @param destination string
   */
  isOrderCapture(): boolean {
    return this.public_url().includes('/capture/');
  }
  base_url(destination) {
    if (this.storeLocation && this.isAdminOrderCaptureUpdate()) {
      destination = `${destination}`;
      return `${this.public_url()}#${destination}`;
    }

    if (this.isOrderCapture() && window.location.hash.includes('#thankyou')) {
      this.router.url.split('#')[0];
      return this.public_url();
    }

    if (LocationService.orderUuid) {
      destination = `orderUuid/${LocationService.orderUuid}/${destination}`;
    }

    // Note: do not reference previous table number when admin order capture.
    if (this.storeLocation && !this.isOrderCapture()) {
      destination = `${destination}`;
    }
    if (destination === '' || window.location.hash.includes('#thankyou')) {
      return this.public_url();
    }
    return `${this.public_url()}#${destination}`;
  }

  /**
   * Opens a bottom-bar page through its fragment, as a tap on its tab does, so
   * the bar highlights it, replacing the current entry rather than stacking one
   * on top of it. Used for a bar page opened by URL (story #10267) and for a
   * template app's start page (story #11856).
   */
  openWidgetPage(fragment: string): void {
    this.router.navigateByUrl(this.base_url(fragment), { replaceUrl: true });
  }

  /**
   * Branded, store-scoped login URL for the customer UI, carrying a `?next=`
   * fragment back to `state`'s widget page when a Template bottom-bar app is
   * the one asking. Shared by `StoreLoadingComponent` (session-expiry while
   * browsing) and `navigateToLogin` (card #11440) so a customer is never sent
   * to the admin-oriented `/login` route.
   */
  customerLoginUrl(state?: string): string | UrlTree {
    const loginUrl = this.base_url('login');
    const nextView = this.helper.isTemplateBottomBarApp()
      ? widgetViewFragmentForState(state)
      : null;
    if (!nextView) {
      return loginUrl;
    }
    const loginTree = this.router.parseUrl(loginUrl);
    loginTree.queryParams = { ...loginTree.queryParams, next: nextView };
    return loginTree;
  }

  /**
   * Whether the page on screen belongs to the customer UI rather than the
   * backoffice (card #11440).
   *
   * The Customer, Template and Kiosk apps only ever render the customer UI, so
   * their app mode settles it. The combined web build serves both UIs from one
   * origin, so there the first path segment decides — the answer must not wait
   * on a store to finish loading, because a session can expire mid-load. Admin
   * order capture is the exception that the path alone would get wrong: it
   * renders a store on behalf of a signed-in backoffice user.
   */
  isCustomerUiView(): boolean {
    if (
      this.helper.isGoApp() ||
      this.helper.isGoTemplateApp() ||
      this.helper.isGoKioskApp()
    ) {
      return true;
    }
    if (this.isOrderCapture() || this.isAdminOrderCaptureUpdate()) {
      return false;
    }
    return isCustomerUiPath(this.public_url());
  }

  /**
   * Sends a signed-out visitor to the login screen that belongs to the UI they
   * are actually in (card #11440). Every caller that reacts to an ended
   * session goes through here — `TokenInterceptor`, `AuthService`, the
   * `LogoutSuccess` effect and `CustomerAuthService` — so one ended session can
   * never produce two navigations that disagree about where the visitor
   * belongs.
   */
  navigateToLogin(state?: string): void {
    if (this.isCustomerUiView()) {
      this.router.navigateByUrl(this.customerLoginUrl(state));
    } else {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Whether the page on screen is one of a Template app's bottom menu bar
   * pages (story #10267), which serve a signed-out customer in place. A
   * customer session that ends there leaves the customer on the page, signed
   * out, the same as one who never signed in (card #11561).
   */
  isBottomBarPageView(): boolean {
    return (
      this.helper.isTemplateBottomBarApp() &&
      isWidgetPageState(this.currentViewState())
    );
  }

  private currentViewState(): string | undefined {
    let viewState: string | undefined;
    this.store
      .select(getCurrentStoreViewStatus)
      .pipe(take(1))
      .subscribe((viewStatus) => (viewState = viewStatus?.state));
    return viewState;
  }

  private currentNavigationId(): number {
    const navState: any = this.location.getState();
    return typeof navState?.navigationId === 'number'
      ? navState.navigationId
      : 1;
  }

  /**
   * Records the in-app history depth of the entry a completed navigation left us
   * on. A push adds an entry to come back to; a replaceUrl or skipLocationChange
   * navigation does not, however much it advances the navigationId. Coming back
   * through popstate restores the depth recorded for the entry on its previous
   * visit, looked up by the id it carried then (restoredState) - the entry's
   * state itself has already been re-stamped with a fresh id by the time this
   * runs. An entry this service has never seen counts as depth 0: falling back
   * to the catalog keeps the customer in the store, which is the safe direction.
   */
  private recordNavigationDepth(): void {
    const navigation = this.router.lastSuccessfulNavigation;
    const trigger = navigation?.trigger;
    const extras = navigation?.extras;

    if (trigger === 'popstate' || trigger === 'hashchange') {
      this.navigationDepth =
        (this.restoredNavigationId !== null
          ? this.navigationDepthByNavId.get(this.restoredNavigationId)
          : undefined) ?? 0;
    } else if (!extras?.replaceUrl && !extras?.skipLocationChange) {
      this.navigationDepth += 1;
    }

    this.restoredNavigationId = null;
    this.navigationDepthByNavId.set(
      this.currentNavigationId(),
      this.navigationDepth,
    );
  }

  /**
   * method to go back to previous page
   * @param fallbackPath | optional, targetted fallback path in case there is no location
   * in the history to go back to, it should include the # for instance '#catalog'
   * defaults to catalog view screen
   */
  goBack(fallbackPath = '') {
    const navState: any = this.location.getState();

    // bypass safari's issues with location history
    if (
      /safari/i.test(this.windowRefSer.nativeWindow.navigator.userAgent) &&
      !/Chrome/i.test(this.windowRefSer.nativeWindow.navigator.userAgent)
    ) {
      // tslint:disable-next-line: no-string-literal
      const plateformLocation: string = (this.location as any)
        ?._locationStrategy?._platformLocation?._location?.href;
      if (
        plateformLocation ===
        `${this.windowRefSer.nativeWindowLocation.origin}${this.router.url}`
      ) {
        this.router.navigateByUrl('');
        return; // Early return after Safari workaround
      }
    }
    try {
      const depth =
        this.navigationDepthByNavId.get(navState?.navigationId) ?? 0;
      if (depth > 0) {
        this.location.back();
      } else {
        // window.history.back() is unsafe here: in a fresh tab (direct URL / deep link)
        // it returns to the browser NTP or the last external page. Always use the
        // catalog fallback when there is no prior Angular navigation. (#11012)
        this.router.navigateByUrl(this.public_url() + fallbackPath);
      }
    } catch (e) {
      this.router.navigateByUrl(this.public_url() + fallbackPath);
    }
  }

  setBasketEnabled(basketEnabled: boolean) {
    this.basketEnabled = basketEnabled;
  }

  setLocale(locale: string) {
    this.locale = locale;
  }

  isBasketEnabled(): boolean {
    return this.basketEnabled;
  }

  isAdminOrderUpdate() {
    return this.router.url.includes('/orders/');
  }
  isAdminOrderCaptureUpdate() {
    return this.router.url.includes('/capture/');
  }

  /**
   * Determines the current admin section based on URL
   * @returns 'marketing' if URL contains '/marketing/', otherwise 'settings'
   */
  getCurrentAdminSection(): 'marketing' | 'settings' {
    const currentUrl = this.windowRefSer.nativeWindowLocation.href;
    return currentUrl.includes('/marketing/') ? 'marketing' : 'settings';
  }

  getSubdomain() {
    const domain = this.windowRefSer.nativeWindowLocation.hostname;

    if (domain.indexOf(environment.envDomain) < 0) {
      return domain;
    }

    if (
      domain.indexOf('.') < 0 ||
      domain.split('.')[0] === 'example' ||
      domain.split('.')[0] === 'lvh' ||
      domain.split('.')[0] === 'www'
    ) {
      return '';
    }
    return domain.split('.')[0];
  }

  isCustomerUI(): boolean {
    const subdomain = this.getSubdomain();
    return !(
      subdomain === '' ||
      subdomain === 'admin' ||
      this.whitelabelService.isWhiteLabelDomainBySubDomain(subdomain)
    );
  }

  setLocationLabel(storeAlias: string, locationUrl: string) {
    if (!storeAlias || !locationUrl) {
      return;
    }
    let cookieExpires = 1;

    if (
      this.locationTypeSelected == LocationType.LOCATION ||
      this.locationTypeSelected == LocationType.ROOM
    ) {
      cookieExpires = 7;
    }
    this.visitCookies.set(
      `location-order-label-${storeAlias}`,
      locationUrl,
      cookieExpires,
    );
    // Invalidate cache to force refresh on next read
    this.invalidateCache();
  }

  setLocationUrlType(
    storeAlias: string,
    locationUrlType: 'url-location' | 'cart-location',
  ) {
    if (!storeAlias || !locationUrlType) {
      return;
    }
    let cookieExpires = 1;

    if (
      this.locationTypeSelected == LocationType.LOCATION ||
      this.locationTypeSelected == LocationType.ROOM
    ) {
      cookieExpires = 7;
    }
    this.visitCookies.set(
      `location-order-type-${storeAlias}`,
      locationUrlType,
      cookieExpires,
    );
    // Invalidate cache to force refresh on next read
    this.invalidateCache();
  }

  removeLocationLabel(storeAlias: string) {
    this.visitCookies.delete(`location-order-label-${storeAlias}`);
    // Invalidate cache after deletion
    this.invalidateCache();
  }

  // Sales Point external_id is captured per-store on customer-storefront bootstrap, persisted in a
  // cookie that lives far longer than the location cookie (kiosks are provisioned once and run for
  // months), and replayed on every subsequent bootstrap so the backend can re-validate it. The
  // cookie holds the external_id rather than the numeric id so the bootstrap endpoint can resolve it
  // case-insensitively. The numeric id surfaces on the bootstrap response and flows from there into
  // the order payload.
  getSalesPointExternalId(storeAlias: string): string | null {
    if (!storeAlias) {
      return null;
    }
    return this.cookieService.get(`sales-point-${storeAlias}`) || null;
  }

  setSalesPointExternalId(storeAlias: string, externalId: string) {
    if (!storeAlias || !externalId) {
      return;
    }
    // 10-year expiry stands in for "indefinite" — ngx-cookie-service treats `undefined` as
    // a session cookie, which would defeat the purpose for a kiosk that is provisioned once.
    const tenYearsFromNow = new Date();
    tenYearsFromNow.setFullYear(tenYearsFromNow.getFullYear() + 10);
    this.cookieService.set(
      `sales-point-${storeAlias}`,
      externalId,
      tenYearsFromNow,
    );
  }

  removeSalesPointExternalId(storeAlias: string) {
    if (!storeAlias) {
      return;
    }
    this.cookieService.delete(`sales-point-${storeAlias}`);
  }

  // The token may sit before the `#` (`/?salespoint=X`) or inside the hash when it
  // is appended to a table link (`/#l/1?salespoint=X`) — the shape the admin hands
  // merchants. Reading only `location.search` missed the second form entirely and
  // gated the customer out of the menu (card #11426).
  //
  // `WindowRefService.nativeWindowLocation` is a live getter returning
  // `window.location` (see window.service.ts), not a value captured at
  // construction — so a read after `stripSalesPointFromUrl()` sees the rewritten
  // URL and will not re-resolve a token that has already been consumed. Because
  // that getter dereferences the global `window`, the `typeof window` guard below
  // is still guarding exactly what this method touches, one level down; the `?.`
  // chain alone would not save a non-browser platform from the ReferenceError
  // thrown inside the getter.
  readSalesPointFromUrl(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const loc = this.windowRefSer?.nativeWindowLocation;
    return getQueryParamFromUrl(loc?.search, loc?.hash, 'salespoint');
  }

  stripSalesPointFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }
    // Same optional chaining as the read above: without it an absent service
    // would throw a TypeError straight into the catch, which would look
    // identical to an unparseable URL.
    const href = this.windowRefSer?.nativeWindowLocation?.href;
    if (!href) {
      return;
    }
    try {
      const cleanUrl = removeQueryParamFromUrl(href, 'salespoint');
      if (!cleanUrl) {
        return;
      }
      window.history.replaceState({}, '', cleanUrl);
    } catch (error) {
      // Swallowed on purpose — a failure here must not break the customer flow.
      // Logged because the cost is invisible otherwise: the consumed token stays
      // in the address bar and survives into whatever the customer shares.
      console.warn(
        '[LocationService] could not strip salespoint from URL',
        error,
      );
    }
  }

  removeLocationUrlType(storeAlias: string) {
    this.visitCookies.delete(`location-order-type-${storeAlias}`);
    // Invalidate cache after deletion
    this.invalidateCache();
  }

  removeLocationOrder(storeAlias: string) {
    this.visitCookies.delete(`location-order-${storeAlias}`);
    // Invalidate cache after deletion
    this.invalidateCache();
  }

  setLocationOrder(
    storeAlias: string,
    orderUuid: string,
    locationLabel: string,
    locationId: number,
    locationType: LocationType,
  ) {
    if (!locationLabel || !locationId) {
      return;
    }

    this.locationTypeSelected = locationType;
    let cookieExpires = 1;

    if (
      this.locationTypeSelected == LocationType.LOCATION ||
      this.locationTypeSelected == LocationType.ROOM
    ) {
      cookieExpires = 7;
    }

    this.visitCookies.set(
      `location-order-${storeAlias}`,
      `${orderUuid}_${locationLabel}_${locationId}`,
      cookieExpires,
    );
    this.setLocationLabel(storeAlias, locationLabel);
    this.setLocationUrlType(storeAlias, this.getLocationType());
    // Invalidate cache after setting new values
    this.invalidateCache();
  }

  getLocationLabel() {
    // Early return for null/undefined cases to prevent unnecessary operations
    if (!this.selectedStore?.aliasName) {
      const urlLocationMatch = this.router.url.match(/\/#l\/([^/?#]+)/);
      return urlLocationMatch ? urlLocationMatch[1] : null;
    }

    // Ensure cache is valid before returning cached value
    this.ensureCacheValid();

    // Return cached value (null-safe, prevents re-renders on empty cookies)
    return this.cachedLocationLabel;
  }

  getLocationType(aliasName?: string): 'url-location' | 'cart-location' {
    // If specific alias is provided, read directly from cookie (rare case)
    if (aliasName && aliasName !== this.selectedStore?.aliasName) {
      const value = this.visitCookies.get(`location-order-type-${aliasName}`);
      return value as 'url-location' | 'cart-location';
    }

    // Early return for null/undefined cases
    if (!this.selectedStore?.aliasName) {
      return null;
    }

    // Ensure cache is valid before returning cached value
    this.ensureCacheValid();

    // Return cached value (null-safe, prevents re-renders on empty cookies)
    return this.cachedLocationType;
  }

  getLocationOrder() {
    // Early return for null/undefined cases to prevent unnecessary operations
    if (!this.selectedStore?.aliasName) {
      return null;
    }

    // Ensure cache is valid before returning cached value
    this.ensureCacheValid();

    // Return cached value (null-safe, prevents re-renders on empty cookies)
    return this.cachedLocationOrder;
  }

  getLocationOrderUuid() {
    const locationOrder = this.getLocationOrder();
    // Null-safe: Only split if locationOrder exists and has content
    return locationOrder ? locationOrder.split('_')[0] : null;
  }

  removeLocationFromUrl(locationLabel: string) {
    try {
      this.location.replaceState(
        buildUrlWithoutLocationLabel(
          this.windowRefSer.nativeWindowLocation.href,
          locationLabel,
        ),
      );
    } catch {
      // no-op: do not break customer flow if URL parsing fails
    }
  }

  hasLocationInUrl(): boolean {
    return hasLocationInHash(this.windowRefSer?.nativeWindowLocation?.hash);
  }

  getCurrentView() {
    return this.currentView;
  }

  getParamAt(index: number): string {
    const params = this.getParams();
    if (params.length <= index) {
      return '';
    }
    return params[index];
  }

  getParams(): string[] {
    const urlParts = this.router.url.split('#');
    if (urlParts.length < 2) {
      return [];
    }
    return urlParts[1].split('/');
  }
}
